import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Animated,
  Image,
  RefreshControl,
  ScrollView,
  StatusBar,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import Icon from 'react-native-vector-icons/Feather';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import AmbientGlow from '../components/AmbientGlow';
import AuraBadge from '../components/AuraBadge';
import VibeVerifiedBadge from '../components/VibeVerifiedBadge';
import { auth } from '../firebase';
import { useDayVibes } from '../hooks/useDayVibes';
import { useDayVibeMatches, EnrichedDayVibeMatch } from '../hooks/useDayVibeMatches';
import { AURA_META } from '../data/dayVibesQuestions';
import {
  ensurePrivateChatForVibeConnection,
  injectVibeIcebreakersOnce,
  resolveVibeConnection,
} from '../utils/vibeSynchronizer';
import type { RootStackParamList } from '../types';
import { Colors, Typography, Radius, Shadow } from '../theme';
import { useI18n } from '../i18n';
import { AURA_TRANSLATION_KEYS, getLocalizedAuraName } from '../utils/auraTranslations';

const LABEL_TEXT: Record<string, string> = {
  instant_sync: 'Instant Sync',
  strong_sync: 'Strong Sync',
  recommended_vibe: 'Recommended Vibe',
};

function useCountdown1PM() {
  const [text, setText] = useState('');
  const [unlocked, setUnlocked] = useState(false);

  useEffect(() => {
    const update = () => {
      const now = new Date();
      if (now.getHours() >= 13) {
        setUnlocked(true);
        setText('');
        return;
      }
      const target = new Date(now);
      target.setHours(13, 0, 0, 0);
      const diff = target.getTime() - now.getTime();
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      const parts: string[] = [];
      if (h > 0) parts.push(`${h}h`);
      parts.push(`${String(m).padStart(2, '0')}m`);
      parts.push(`${String(s).padStart(2, '0')}s`);
      setText(parts.join(' '));
      setUnlocked(false);
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, []);

  return { countdown: text, unlocked };
}

// ── Trait Chip ───────────────────────────────────────────────────────────────

function TraitChip({ label, color }: { label: string; color: string }) {
  return (
    <View style={[styles.traitChip, { borderColor: color + '40', backgroundColor: color + '14' }]}>
      <Text style={[styles.traitChipText, { color }]}>{label}</Text>
    </View>
  );
}

// ── Vibe Card ───────────────────────────────────────────────────────────────

function MatchCard({
  match,
  onAction,
}: {
  match: EnrichedDayVibeMatch;
  onAction: () => void;
}) {
  const { t, language } = useI18n();
  const LABEL_TEXT_I18N: Record<string, string> = {
    instant_sync: t('vibes.instantSync'),
    strong_sync: t('vibes.strongSync'),
    recommended_vibe: t('vibes.recommendedVibe'),
  };
  const meta = AURA_META[match.otherUserAuraType];
  const isTwin = match.alignmentLabel === 'instant_sync';
  const isStrong = match.alignmentLabel === 'strong_sync';
  const twinColor = '#C084FC';
  const strongColor = '#26D0CE';
  const recommendedColor = '#F27D26';
  const accentColor = isTwin ? twinColor : isStrong ? strongColor : recommendedColor;
  const labelText = LABEL_TEXT_I18N[match.alignmentLabel] ?? LABEL_TEXT[match.alignmentLabel];
  const visiblePercent = Math.min(match.matchPercent, 99);
  const otherAuraTranslation = AURA_TRANSLATION_KEYS[match.otherUserAuraType];
  const localizedOtherAuraName = getLocalizedAuraName(match.otherUserAuraType, t);
  const localizedSharedTraits = match.sharedTraits.map(trait => {
    const traitIndex = AURA_META[match.otherUserAuraType].traits.indexOf(trait);
    return traitIndex >= 0 ? t(otherAuraTranslation.traits[traitIndex]) : trait;
  });
  const localizedActionLabel =
    match.actionLabel === 'Open chat' ? t('vibes.openChat') :
    match.actionLabel === 'Continue chat' ? t('vibes.continueChat') :
    match.actionLabel === 'Send vibe' ? t('vibes.sendVibe') :
    match.actionLabel === 'Start sync' ? t('vibes.startSync') :
    match.actionLabel;
  const localizedSharedReason = language !== 'en'
    ? `${t('vibes.alignmentPercentPrefix')} ${visiblePercent}%.`
    : match.sharedReason;
  const previewIcebreakers = isTwin || isStrong ? match.icebreakers.slice(0, 2) : match.icebreakers.slice(0, 1);
  const localizedIcebreakers = language !== 'en'
    ? [t('vibes.icebreakerPrompt1'), t('vibes.icebreakerPrompt2')].slice(0, previewIcebreakers.length)
    : previewIcebreakers;

  return (
    <TouchableOpacity
      onPress={onAction}
      activeOpacity={0.88}
      style={[styles.vibeCard, isTwin && styles.vibeCardInstant]}
    >
      {isTwin && (
        <LinearGradient
          colors={['rgba(192,132,252,0.15)', 'rgba(129,140,248,0.06)', 'rgba(7,11,20,0)']}
          locations={[0, 0.5, 1]}
          style={[StyleSheet.absoluteFill, { borderRadius: 20 }]}
        />
      )}

      {/* Type label */}
      <View style={[styles.labelBadge, { borderColor: accentColor + '40', backgroundColor: accentColor + '14' }]}>
        {isTwin && <Icon name="zap" size={10} color={accentColor} />}
        <Text style={[styles.labelBadgeText, { color: accentColor }]}>
          {labelText}
        </Text>
      </View>

      <View style={styles.vibeCardTop}>
        {/* Avatar */}
        <View style={[styles.avatarWrap, { borderColor: meta.color + '55' }]}>
          {match.otherUserPhoto ? (
            <Image source={{ uri: match.otherUserPhoto }} style={styles.avatarImg} />
          ) : (
            <LinearGradient colors={[meta.gradient[0], meta.gradient[1]]} style={styles.avatarPlaceholder}>
              <Icon name="user" size={22} color={meta.color} />
            </LinearGradient>
          )}
          <View style={[styles.auraDot, { backgroundColor: meta.color }]} />
          {/* auraDot already owns bottom-right — verified badge moved to
              bottom-left to avoid overlapping it. */}
          <VibeVerifiedBadge
            verified={match.otherUserVibeVerified === true}
            size={52}
            style={styles.matchAvatarBadgePosition}
          />
        </View>

        {/* Name + aura */}
        <View style={styles.vibeCardInfo}>
          <Text style={styles.vibeCardName}>{match.otherUserName === 'Traveler' ? t('chat.traveler') : match.otherUserName}</Text>
          <Text style={[styles.vibeCardAura, { color: meta.color }]}>{localizedOtherAuraName}</Text>
        </View>

        {/* Vibe alignment */}
        <View style={[styles.alignmentCircle, { borderColor: accentColor + '55' }]}>
          <Text style={[styles.alignmentPct, { color: accentColor }]}>{visiblePercent}%</Text>
          <Text style={styles.alignmentWord}>{t('vibes.sync')}</Text>
        </View>
      </View>

      {/* Shared reason */}
      <Text style={styles.sharedReason}>{localizedSharedReason}</Text>

      {/* Shared traits */}
      {match.sharedTraits.length > 0 && (
        <View style={styles.sharedTraitsRow}>
          {localizedSharedTraits.map((trait, idx) => (
            <View key={idx} style={styles.sharedTraitChip}>
              <Text style={styles.sharedTraitText}>{trait}</Text>
            </View>
          ))}
        </View>
      )}

      {/* CTA */}
      <TouchableOpacity
        onPress={onAction}
        style={[styles.openVibeBtn, isTwin && styles.openVibeBtnInstant]}
        activeOpacity={0.82}
      >
        {isTwin && (
          <LinearGradient
            colors={['rgba(192,132,252,0.24)', 'rgba(129,140,248,0.14)']}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            style={StyleSheet.absoluteFill}
          />
        )}
        <Text style={[styles.openVibeBtnText, { color: isTwin ? twinColor : 'rgba(255,255,255,0.55)' }]}>
          {localizedActionLabel}
        </Text>
        <Icon
          name={match.connection.chatId ? 'message-circle' : 'arrow-right'}
          size={13}
          color={isTwin ? twinColor : 'rgba(255,255,255,0.5)'}
        />
      </TouchableOpacity>

      {previewIcebreakers.length > 0 && (
        <View style={styles.icebreakerNote}>
          <Icon name="message-square" size={12} color="rgba(192,132,252,0.6)" />
          <View style={styles.icebreakerCopy}>
            <Text style={styles.icebreakerText}>
              {isTwin ? t('vibes.icebreakersReady') : t('vibes.suggestedIcebreaker')}
            </Text>
            {localizedIcebreakers.map((prompt, index) => (
              <Text key={`${match.id}-prompt-${index}`} style={styles.icebreakerPrompt}>{prompt}</Text>
            ))}
          </View>
        </View>
      )}
    </TouchableOpacity>
  );
}

// ── Main Screen ───────────────────────────────────────────────────────────────

export default function DayVibesAuraScreen() {
  const { t } = useI18n();
  const insets = useSafeAreaInsets();
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const uid = auth.currentUser?.uid ?? null;
  const { submission, state, loading } = useDayVibes(uid);
  const matchesUnlockedByState = state === 'submitted_unlocked';
  const { matches, loading: matchesLoading, matchingDone, matchingRetrying, reload: reloadMatches } = useDayVibeMatches(
    uid,
    matchesUnlockedByState ? submission : null,
  );
  const { countdown, unlocked: matchesUnlockedByClock } = useCountdown1PM();
  const [refreshing, setRefreshing] = useState(false);

  const enterAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.timing(enterAnim, {
      toValue: 1,
      duration: 700,
      delay: 200,
      useNativeDriver: true,
    }).start();
  }, [enterAnim]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await reloadMatches();
    setRefreshing(false);
  }, [reloadMatches]);

  const auratwins = matches.filter(m => m.alignmentLabel === 'instant_sync');
  const strongMatches = matches.filter(m => m.alignmentLabel !== 'instant_sync');
  const matchesUnlocked = matchesUnlockedByState || matchesUnlockedByClock;

  const handleMatchAction = async (match: EnrichedDayVibeMatch) => {
    if (!uid) return;
    const latestConnection = await resolveVibeConnection(uid, match.otherUserId);
    const shouldOpenChat =
      match.alignmentLabel === 'instant_sync' ||
      latestConnection.chatId ||
      latestConnection.status === 'accepted_vibe' ||
      latestConnection.status === 'legacy_match';

    if (!shouldOpenChat) {
      navigation.navigate('PublicProfile', { userId: match.otherUserId });
      return;
    }

    const connection = latestConnection.chatId && latestConnection.chatKind
      ? latestConnection
      : await ensurePrivateChatForVibeConnection(uid, match.otherUserId, latestConnection);

    if (connection.chatId && connection.chatKind) {
      if (connection.chatKind === 'vibe' && match.alignmentLabel === 'instant_sync') {
        await injectVibeIcebreakersOnce({
          dateKey: match.dateKey,
          userA: uid,
          userB: match.otherUserId,
          chatId: connection.chatId,
          similarityPercent: match.matchPercent,
          icebreakers: match.icebreakers.slice(0, 2),
        }).catch(error => console.warn('[Tonyte][RN][VibeSynchronizer] icebreaker injection failed', {
          chatId: connection.chatId,
          dateKey: match.dateKey,
          error,
        }));
      }
      console.info('[Tonyte][RN][VibeSynchronizer] opening existing chat', {
        chatId: connection.chatId,
        chatKind: connection.chatKind,
        targetUserId: match.otherUserId,
      });
      navigation.navigate('ChatDetail', {
        chatId: connection.chatId,
        name: match.otherUserName,
        kind: connection.chatKind,
        photo: match.otherUserPhoto,
        participantIds: [uid ?? '', match.otherUserId],
      });
    } else {
      navigation.navigate('PublicProfile', { userId: match.otherUserId });
    }
  };

  const enterStyle = {
    opacity: enterAnim,
    transform: [{ translateY: enterAnim.interpolate({ inputRange: [0, 1], outputRange: [18, 0] }) }],
  };

  if (loading) {
    return (
      <View style={[styles.container, styles.center]}>
        <LinearGradient colors={['#07101d', '#0a0c1a']} style={StyleSheet.absoluteFill} />
        <Icon name="activity" size={32} color={Colors.turquoise} />
        <Text style={styles.noDataText}>{t('vibes.loadingAura')}</Text>
      </View>
    );
  }

  if (state === 'locked_until_open') {
    return (
      <View style={styles.container}>
        <LinearGradient colors={['#07101d', '#080c1a', '#070b14']} style={StyleSheet.absoluteFill} />
        <AmbientGlow tone="orange" size={360} intensity={0.24} style={styles.bgGlowTop} />
        <AmbientGlow tone="blue" size={320} intensity={0.2} style={styles.bgGlowBottom} />
        <View style={[styles.lockedRouteWrap, { paddingTop: insets.top + 24, paddingBottom: insets.bottom + 24 }]}>
          <TouchableOpacity onPress={() => navigation.goBack()} style={styles.closeBtn} activeOpacity={0.7}>
            <Icon name="arrow-left" size={17} color="rgba(255,255,255,0.65)" />
          </TouchableOpacity>
          <View style={styles.lockedRouteCard}>
            <LinearGradient
              colors={['rgba(255,255,255,0.13)', 'rgba(15,21,32,0.88)', 'rgba(5,8,18,0.96)']}
              locations={[0, 0.42, 1]}
              style={StyleSheet.absoluteFill}
            />
            <View style={styles.lockedRouteBadge}>
              <Icon name="sunrise" size={11} color="rgba(255,213,128,0.9)" />
              <Text style={styles.lockedRouteBadgeText}>{t('vibes.dayVibesBadge')}</Text>
            </View>
            <LinearGradient colors={['#FFB347', '#C084FC', '#26D0CE']} style={styles.lockedRouteOrb}>
              <View style={styles.lockedRouteOrbCore}>
                <Icon name="lock" size={26} color="rgba(255,255,255,0.9)" />
              </View>
            </LinearGradient>
            <Text style={styles.lockedRouteTitle}>{t('vibes.openAt6am')}</Text>
            <Text style={styles.lockedRouteText}>{t('vibes.resetDaily')}</Text>
          </View>
        </View>
      </View>
    );
  }

  if (!submission) {
    return (
      <View style={[styles.container, styles.center]}>
        <LinearGradient colors={['#07101d', '#0a0c1a']} style={StyleSheet.absoluteFill} />
        <Icon name="sun" size={32} color={Colors.sunset} />
        <Text style={styles.noDataText}>{t('vibes.noAuraToday')}</Text>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backLink}>
          <Text style={styles.backLinkText}>{t('vibes.goBack')}</Text>
        </TouchableOpacity>
      </View>
    );
  }

  const auraMeta = AURA_META[submission.auraType];
  const auraTranslation = AURA_TRANSLATION_KEYS[submission.auraType];
  const localizedAuraName = getLocalizedAuraName(submission.auraType, t);
  const localizedAuraDescription = t(auraTranslation.description);
  const localizedAuraTraits = auraTranslation.traits.map(key => t(key));

  return (
    <View style={styles.container}>
      <StatusBar barStyle="light-content" translucent backgroundColor="transparent" />

      {/* Background */}
      <LinearGradient
        colors={['#07101d', '#080c1a', '#070b14']}
        style={StyleSheet.absoluteFill}
      />
      <AmbientGlow tone="blue" size={400} intensity={0.28} style={styles.bgGlowTop} />
      <AmbientGlow tone="orange" size={300} intensity={0.18} style={styles.bgGlowBottom} />
      {/* Aura-specific glow */}
      <View
        style={[
          styles.auraGlowBg,
          { backgroundColor: auraMeta.glowColor },
        ]}
      />

      <ScrollView
        style={styles.scroll}
        contentContainerStyle={[
          styles.scrollContent,
          { paddingTop: insets.top + 12, paddingBottom: insets.bottom + 120 },
        ]}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={Colors.purple} />
        }
      >
        {/* Header */}
        <View style={styles.headerRow}>
          <TouchableOpacity onPress={() => navigation.goBack()} style={styles.closeBtn} activeOpacity={0.7}>
            <Icon name="x" size={18} color="rgba(255,255,255,0.65)" />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>{t('vibes.todaysAura')}</Text>
          <View style={{ width: 36 }} />
        </View>

        {/* Aura badge + identity */}
        <Animated.View style={[styles.auraIdentity, enterStyle]}>
          <AuraBadge auraType={submission.auraType} />

          <View style={styles.auraTextBlock}>
            <Text style={styles.auraKicker}>{t('vibes.todayYouAre')}</Text>
            <Text style={[styles.auraName, { color: auraMeta.color }]}>{localizedAuraName}</Text>
            <Text style={styles.auraDescription}>{localizedAuraDescription}</Text>
          </View>

          {/* Traits */}
          <View style={styles.traitsRow}>
            {localizedAuraTraits.map((trait, idx) => (
              <TraitChip key={idx} label={trait} color={auraMeta.color} />
            ))}
          </View>
        </Animated.View>

        {/* Divider */}
        <View style={styles.divider}>
          <View style={[styles.dividerLine, { backgroundColor: auraMeta.color + '28' }]} />
        </View>

        {!matchesUnlocked && (
          <Animated.View style={[styles.lockedPanel, enterStyle]}>
            <LinearGradient
              colors={['rgba(192,132,252,0.08)', 'rgba(15,21,32,0.9)']}
              style={[StyleSheet.absoluteFill, { borderRadius: 24 }]}
            />
            <View style={styles.lockedIconWrap}>
              <Icon name="clock" size={24} color="rgba(192,132,252,0.75)" />
            </View>
            <Text style={styles.lockedTitle}>{t('vibes.firstVibesUnlock')}</Text>
            <Text style={styles.lockedBody}>
              {t('vibes.moreMatchesLater')}
            </Text>
            {countdown ? (
              <View style={styles.countdownWrap}>
                <Icon name="clock" size={13} color="rgba(255,255,255,0.4)" />
                <Text style={styles.countdownText}>{t('vibes.resultsIn')} {countdown}</Text>
              </View>
            ) : null}
          </Animated.View>
        )}

        {/* Vibe Circle results */}
        {matchesUnlocked && (
          <Animated.View style={[{ gap: 12 }, enterStyle]}>
          {/* Section header */}
          <View style={styles.vibeCircleHeader}>
            <View style={styles.vibeCircleTitleRow}>
              <View style={styles.frequencyDot} />
              <Text style={styles.vibeCircleTitle}>{t('vibes.vibeCircle')}</Text>
            </View>
            <Text style={styles.vibeCircleSub}>{t('vibes.basedOnToday')}</Text>
          </View>

            {/* Loading the list */}
            {matchesLoading && (
              <View style={styles.loadingPanel}>
                <Text style={styles.loadingText}>{t('vibes.findingWavelength')}</Text>
              </View>
            )}

            {/* Matching still running for this player (results open, not yet compared with everyone) */}
            {!matchesLoading && matches.length === 0 && !matchingDone && (
              <View style={styles.emptyPanel} accessibilityLiveRegion="polite">
                <ActivityIndicator color={Colors.turquoise} />
                <Text style={styles.emptyTitle}>{t('vibes.matchingInProgress')}</Text>
                <Text style={styles.emptyBody}>{t(matchingRetrying ? 'vibes.matchingRetryingBody' : 'vibes.matchingInProgressBody')}</Text>
              </View>
            )}

            {/* Matched against everyone so far — nobody compatible yet */}
            {!matchesLoading && matches.length === 0 && matchingDone && (
              <View style={styles.emptyPanel}>
                <Icon name="zap" size={28} color="rgba(255,255,255,0.2)" />
                <Text style={styles.emptyTitle}>{t('vibes.noAlignmentYet')}</Text>
                <Text style={styles.emptyBody}>
                  {t('vibes.noAlignmentSubtitle')}
                </Text>
                <TouchableOpacity onPress={onRefresh} style={styles.refreshBtn}>
                  <Icon name="refresh-cw" size={14} color={Colors.turquoise} />
                  <Text style={styles.refreshBtnText}>{t('vibes.refresh')}</Text>
                </TouchableOpacity>
              </View>
            )}

            {/* Instant Sync */}
            {auratwins.length > 0 && (
              <>
                <View style={styles.matchTierRow}>
                  <Icon name="zap" size={12} color="#C084FC" />
                  <Text style={[styles.matchTierLabel, { color: '#C084FC' }]}>{t('vibes.instantSync')}</Text>
                </View>
                {auratwins.map(m => (
                  <MatchCard key={m.id} match={m} onAction={() => handleMatchAction(m)} />
                ))}
              </>
            )}

            {/* Strong and recommended alignment */}
            {strongMatches.length > 0 && (
              <>
                <View style={styles.matchTierRow}>
                  <Icon name="radio" size={12} color="#26D0CE" />
                  <Text style={[styles.matchTierLabel, { color: '#26D0CE' }]}>{t('vibes.vibeAlignment')}</Text>
                </View>
                {strongMatches.map(m => (
                  <MatchCard key={m.id} match={m} onAction={() => handleMatchAction(m)} />
                ))}
              </>
            )}

            {matches.length > 0 && (
              <Text style={styles.circleFooter}>
                {matches.length} {matches.length === 1
                  ? t('vibes.sharedFrequencyToday')
                  : t('vibes.sharedFrequenciesToday')}
                {' · '}
                {t('vibes.moreCanArrive')}
              </Text>
            )}
          </Animated.View>
        )}

      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#070b14' },
  center: { alignItems: 'center', justifyContent: 'center', gap: 12 },
  scroll: { flex: 1 },
  scrollContent: { paddingHorizontal: 20, gap: 20 },

  bgGlowTop: { position: 'absolute', top: -180, left: -80 },
  bgGlowBottom: { position: 'absolute', bottom: -160, right: -80 },
  auraGlowBg: {
    position: 'absolute',
    width: 320,
    height: 320,
    borderRadius: 160,
    top: -80,
    alignSelf: 'center',
    opacity: 0.12,
    filter: [{ blur: 80 }],
  },

  // Header
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  closeBtn: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(255,255,255,0.06)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    color: Colors.textPrimary,
    fontSize: Typography.base,
    fontWeight: Typography.extrabold,
    letterSpacing: 0.3,
  },

  // Aura identity block
  auraIdentity: {
    alignItems: 'center',
    gap: 20,
    paddingTop: 12,
  },

  // Aura text
  auraTextBlock: { alignItems: 'center', gap: 8, paddingHorizontal: 8 },
  auraKicker: {
    color: 'rgba(255,255,255,0.3)',
    fontSize: 9,
    fontWeight: Typography.extrabold,
    letterSpacing: 2.8,
  },
  auraName: {
    fontSize: 32,
    fontWeight: Typography.extrabold,
    textAlign: 'center',
    letterSpacing: 0.4,
  },
  auraDescription: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: Typography.sm,
    lineHeight: 20,
    textAlign: 'center',
    fontStyle: 'italic',
  },

  // Traits
  traitsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 8,
  },
  traitChip: {
    paddingHorizontal: 12,
    paddingVertical: 5,
    borderRadius: Radius.pill,
    borderWidth: 1,
  },
  traitChipText: {
    fontSize: Typography.xs,
    fontWeight: Typography.semibold,
  },

  // Divider
  divider: { paddingHorizontal: 32 },
  dividerLine: { height: 1, borderRadius: 1 },
  lockedRouteWrap: {
    flex: 1,
    paddingHorizontal: 20,
    justifyContent: 'center',
    gap: 28,
  },
  lockedRouteCard: {
    minHeight: 340,
    borderRadius: 28,
    paddingHorizontal: 22,
    paddingVertical: 28,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 20,
    backgroundColor: 'rgba(15,21,32,0.76)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.13)',
    overflow: 'hidden',
    shadowColor: '#C084FC',
    shadowOpacity: 0.18,
    shadowRadius: 28,
    shadowOffset: { width: 0, height: 14 },
    elevation: 10,
  },
  lockedRouteBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 11,
    paddingVertical: 5,
    borderRadius: Radius.pill,
    backgroundColor: 'rgba(255,179,71,0.08)',
    borderWidth: 1,
    borderColor: 'rgba(255,179,71,0.18)',
  },
  lockedRouteBadgeText: {
    color: 'rgba(255,213,128,0.9)',
    fontSize: 9,
    fontWeight: Typography.extrabold,
    letterSpacing: 1.8,
  },
  lockedRouteOrb: {
    width: 96,
    height: 96,
    borderRadius: 48,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#C084FC',
    shadowColor: '#FFB347',
    shadowOpacity: 0.45,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: 0 },
    elevation: 12,
  },
  lockedRouteOrbCore: {
    width: 74,
    height: 74,
    borderRadius: 37,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(7,11,20,0.64)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.22)',
  },
  lockedRouteTitle: {
    color: Colors.textPrimary,
    fontSize: 24,
    lineHeight: 30,
    fontWeight: Typography.extrabold,
    textAlign: 'center',
  },
  lockedRouteText: {
    color: 'rgba(224,242,254,0.58)',
    fontSize: Typography.sm,
    lineHeight: 20,
    textAlign: 'center',
  },

  // Locked panel
  lockedPanel: {
    borderRadius: 24,
    borderWidth: 1,
    borderColor: 'rgba(192,132,252,0.18)',
    padding: 24,
    alignItems: 'center',
    gap: 12,
    overflow: 'hidden',
  },
  lockedIconWrap: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: 'rgba(192,132,252,0.1)',
    borderWidth: 1,
    borderColor: 'rgba(192,132,252,0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  lockedTitle: {
    color: Colors.textPrimary,
    fontSize: Typography.lg,
    fontWeight: Typography.extrabold,
    textAlign: 'center',
  },
  lockedBody: {
    color: 'rgba(255,255,255,0.55)',
    fontSize: Typography.sm,
    lineHeight: 20,
    textAlign: 'center',
  },
  countdownWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    maxWidth: '100%',
    gap: 6,
    paddingHorizontal: 14,
    paddingVertical: 7,
    borderRadius: Radius.pill,
    backgroundColor: 'rgba(255,255,255,0.06)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
  },
  countdownText: {
    flexShrink: 1,
    color: 'rgba(255,255,255,0.55)',
    fontSize: Typography.sm,
    fontWeight: Typography.semibold,
    fontVariant: ['tabular-nums'],
    textAlign: 'center',
  },
  // Match tier labels
  matchTierRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 4,
  },
  matchTierLabel: {
    fontSize: Typography.xs,
    fontWeight: Typography.extrabold,
    letterSpacing: 1.2,
    textTransform: 'uppercase',
  },

  // Vibe circle / match section header
  vibeCircleHeader: { gap: 6 },
  vibeCircleTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  frequencyDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#26D0CE',
    shadowColor: '#26D0CE',
    shadowOpacity: 0.7,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 0 },
  },
  vibeCircleTitle: {
    color: Colors.textPrimary,
    fontSize: Typography.lg,
    fontWeight: Typography.extrabold,
  },
  vibeCircleSub: {
    color: 'rgba(255,255,255,0.4)',
    fontSize: Typography.sm,
    paddingLeft: 16,
  },

  // Vibe card
  vibeCard: {
    borderRadius: 20,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(15,21,32,0.7)',
    padding: 16,
    gap: 12,
    overflow: 'hidden',
    ...Shadow.card,
  },
  vibeCardInstant: {
    borderColor: 'rgba(192,132,252,0.35)',
    shadowColor: '#C084FC',
    shadowOpacity: 0.22,
    shadowRadius: 18,
  },
  labelBadge: {
    alignSelf: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 3,
    borderRadius: Radius.pill,
    borderWidth: 1,
  },
  labelBadgeText: {
    fontSize: 10,
    fontWeight: Typography.extrabold,
    letterSpacing: 0.6,
  },
  vibeCardTop: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  avatarWrap: {
    width: 52,
    height: 52,
    borderRadius: 26,
    borderWidth: 1.5,
    overflow: 'visible',
  },
  avatarImg: {
    width: 52,
    height: 52,
    borderRadius: 26,
  },
  avatarPlaceholder: {
    width: 52,
    height: 52,
    borderRadius: 26,
    alignItems: 'center',
    justifyContent: 'center',
  },
  matchAvatarBadgePosition: {
    right: undefined,
    left: -2,
  },
  auraDot: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: 14,
    height: 14,
    borderRadius: 7,
    borderWidth: 2,
    borderColor: '#070b14',
  },
  vibeCardInfo: { flex: 1, gap: 3 },
  vibeCardName: {
    color: Colors.textPrimary,
    fontSize: Typography.base,
    fontWeight: Typography.bold,
  },
  vibeCardAura: {
    fontSize: Typography.xs,
    fontWeight: Typography.semibold,
  },
  alignmentCircle: {
    width: 56,
    height: 56,
    borderRadius: 28,
    borderWidth: 1.5,
    backgroundColor: 'rgba(255,255,255,0.04)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  alignmentPct: {
    fontSize: Typography.sm,
    fontWeight: Typography.extrabold,
  },
  alignmentWord: {
    color: 'rgba(255,255,255,0.3)',
    fontSize: 8,
    fontWeight: Typography.semibold,
    letterSpacing: 0.3,
  },
  sharedReason: {
    color: 'rgba(255,255,255,0.52)',
    fontSize: Typography.xs,
    lineHeight: 18,
    fontStyle: 'italic',
  },
  sharedTraitsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  sharedTraitChip: {
    paddingHorizontal: 10,
    paddingVertical: 3,
    borderRadius: Radius.pill,
    backgroundColor: 'rgba(255,255,255,0.07)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
  },
  sharedTraitText: {
    color: 'rgba(255,255,255,0.55)',
    fontSize: 10,
    fontWeight: Typography.semibold,
  },
  openVibeBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 10,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(255,255,255,0.04)',
    overflow: 'hidden',
  },
  openVibeBtnInstant: {
    borderColor: 'rgba(192,132,252,0.28)',
  },
  openVibeBtnText: {
    color: 'rgba(255,255,255,0.55)',
    fontSize: Typography.xs,
    fontWeight: Typography.extrabold,
    letterSpacing: 0.3,
  },
  icebreakerNote: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 6,
  },
  icebreakerCopy: {
    flex: 1,
    gap: 4,
  },
  icebreakerText: {
    color: 'rgba(192,132,252,0.6)',
    fontSize: Typography.xs,
    fontStyle: 'italic',
  },
  icebreakerPrompt: {
    color: 'rgba(255,255,255,0.62)',
    fontSize: 11,
    lineHeight: 15,
  },

  // Loading / empty
  loadingPanel: {
    alignItems: 'center',
    padding: 32,
  },
  loadingText: {
    color: 'rgba(255,255,255,0.3)',
    fontSize: Typography.sm,
    fontStyle: 'italic',
  },
  emptyPanel: {
    alignItems: 'center',
    padding: 32,
    gap: 10,
  },
  emptyTitle: {
    color: 'rgba(255,255,255,0.5)',
    fontSize: Typography.base,
    fontWeight: Typography.semibold,
    textAlign: 'center',
  },
  emptyBody: {
    color: 'rgba(255,255,255,0.3)',
    fontSize: Typography.sm,
    textAlign: 'center',
    lineHeight: 20,
  },
  refreshBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: Radius.pill,
    backgroundColor: 'rgba(38,208,206,0.1)',
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.2)',
  },
  refreshBtnText: {
    color: Colors.turquoise,
    fontSize: Typography.xs,
    fontWeight: Typography.semibold,
  },

  // Footer
  circleFooter: {
    textAlign: 'center',
    color: 'rgba(255,255,255,0.18)',
    fontSize: Typography.xs,
  },

  // No-submission fallback
  noDataText: {
    color: 'rgba(255,255,255,0.5)',
    fontSize: Typography.base,
  },
  backLink: { paddingVertical: 8, paddingHorizontal: 16 },
  backLinkText: { color: Colors.turquoise, fontSize: Typography.sm },
});
